'use client';

import { type RefObject, useEffect } from 'react';

/**
 * Workaround for the `inverted` + macOS-natural-scroll UX trap.
 *
 * When `transform: scaleY(-1)` is applied to the scroller, the browser
 * doesn't reinterpret wheel/trackpad input — scrollTop still increases
 * for a "scroll down" gesture. On macOS with natural scrolling that means
 * a two-finger swipe DOWN (which mentally pulls older content from above
 * into view) lands at scrollTop=0 and gets stuck; users have to swipe UP
 * to reveal history, which feels backwards.
 *
 * The fix is the same as the PopshopRN one
 * (src/functions/ux/useInvertedScrollFix.tsx) — references
 * https://github.com/Shopify/flash-list/issues/1351:
 *   - intercept the wheel event with `passive: false`
 *   - preventDefault to stop the browser's default scroll
 *   - apply `scrollTop -= deltaY` so the gesture maps to the
 *     direction the user expects in an inverted layout
 *
 * Opt-in via FlashList's `invertedScrollFix` prop. Default off — the prop
 * exists because the right answer depends on the consumer's data shape:
 *   - chat with `inverted` + newest-first data → enable
 *   - inverted lists with different mental models → leave off
 */
export function useInvertedScrollFix(
  scrollerRef: RefObject<HTMLDivElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      scroller.scrollTop -= event.deltaY;
    };

    scroller.addEventListener('wheel', handleWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', handleWheel);
  }, [active, scrollerRef]);
}
